type JsonObject = Record<string, unknown>;

const TRANSLATION_PAGES = new Set(['home', 'presse', 'autor-robert-litwak', 'haus-und-grund', 'haus-und-grund-duesseldorf', 'immoscout24']);
const LEGAL_PAGES = new Set(['impressum', 'datenschutz', 'agb']);

export function deepMerge(target: JsonObject, source: JsonObject): JsonObject {
    const result: JsonObject = { ...target };

    for (const [key, value] of Object.entries(source)) {
        if (key === '__proto__' || key === 'constructor' || key === 'prototype') {
            continue;
        }

        if (
            value !== null
            && typeof value === 'object'
            && !Array.isArray(value)
            && result[key] !== null
            && typeof result[key] === 'object'
            && !Array.isArray(result[key])
        ) {
            result[key] = deepMerge(result[key] as JsonObject, value as JsonObject);
        } else {
            result[key] = value;
        }
    }

    return result;
}

/**
 * Removes SEO-only fields from page JSON before merging into i18n namespaces.
 */
export function stripPageSeoMetadata(data: JsonObject): JsonObject {
    const content = { ...data };

    if (content.meta !== null && typeof content.meta === 'object' && !Array.isArray(content.meta)) {
        const meta = { ...(content.meta as JsonObject) };
        delete meta.published;
        content.meta = meta;
    }

    return content;
}

export function namespaceForPage(pageName: string): 'translation' | 'marketing-pages' | 'legal' {
    if (TRANSLATION_PAGES.has(pageName)) {
        return 'translation';
    }

    if (LEGAL_PAGES.has(pageName)) {
        return 'legal';
    }

    return 'marketing-pages';
}

export interface MergedPublicLocales {
    translation: JsonObject;
    'marketing-pages': JsonObject;
    legal: JsonObject;
}

/**
 * Merges `public/common.json` and `public/pages/*.json` into i18n namespace objects.
 */
export function mergePublicLocaleFiles(
    common: JsonObject,
    pages: Record<string, JsonObject>,
): MergedPublicLocales {
    const merged: MergedPublicLocales = {
        translation: {},
        'marketing-pages': {},
        legal: {},
    };

    if (common.translation && typeof common.translation === 'object') {
        merged.translation = deepMerge(merged.translation, common.translation as JsonObject);
    }

    if (common['marketing-pages'] && typeof common['marketing-pages'] === 'object') {
        merged['marketing-pages'] = deepMerge(
            merged['marketing-pages'],
            common['marketing-pages'] as JsonObject,
        );
    }

    for (const [pageName, pageData] of Object.entries(pages)) {
        const namespace = namespaceForPage(pageName);

        merged[namespace] = deepMerge(merged[namespace], {
            [pageName]: stripPageSeoMetadata(pageData),
        });
    }

    return merged;
}
